import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  NewReleases as LatestIcon,
} from '@mui/icons-material';
import { format, isValid, parseISO } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { parseChangelog, type ChangelogRelease } from '../../utils/parseChangelog';
import { useChangelogMarkdownComponents } from './changelogMarkdown';

const INITIAL_VISIBLE = 12;

function formatReleaseDate(date: string): string {
  const parsed = parseISO(date);
  if (!isValid(parsed)) return date;
  return format(parsed, 'd MMM yyyy');
}

function ReleaseTimelineItem({
  release,
  isLatest,
  defaultExpanded,
}: {
  release: ChangelogRelease;
  isLatest: boolean;
  defaultExpanded: boolean;
}) {
  const theme = useTheme();
  const markdownComponents = useChangelogMarkdownComponents();

  return (
    <Box sx={{ position: 'relative', pl: { xs: 0, sm: 4 }, mb: 1.5 }}>
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'absolute',
          left: 10,
          top: 22,
          width: 12,
          height: 12,
          borderRadius: '50%',
          bgcolor: isLatest ? 'secondary.main' : 'background.paper',
          border: 3,
          borderColor: isLatest ? 'secondary.main' : alpha(theme.palette.secondary.main, 0.45),
          zIndex: 1,
        }}
      />

      <Accordion
        defaultExpanded={defaultExpanded}
        disableGutters
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 0,
          bgcolor: 'background.paper',
          '&::before': { display: 'none' },
          '&.Mui-expanded': { borderColor: 'divider' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
          sx={{
            px: { xs: 2, md: 3 },
            py: 0.5,
            minHeight: 56,
            bgcolor: isLatest ? '#1e3a5c' : 'transparent',
            '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
            '& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root': {
              color: isLatest ? '#ffffff' : 'text.secondary',
            },
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            sx={{ width: '100%', pr: 1 }}
          >
            <Chip
              label={`v${release.version}`}
              size="small"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.02em',
                bgcolor: isLatest ? 'rgba(255,255,255,0.15)' : alpha(theme.palette.primary.main, 0.08),
                color: isLatest ? '#ffffff' : 'primary.main',
                borderRadius: 1,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color: isLatest ? 'rgba(255,255,255,0.7)' : 'text.secondary',
              }}
            >
              {formatReleaseDate(release.date)}
            </Typography>
            {isLatest && (
              <Chip
                icon={<LatestIcon sx={{ fontSize: '14px !important', color: '#ffffff !important' }} />}
                label="Latest"
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, color: '#ffffff', borderColor: 'rgba(255,255,255,0.4)', borderRadius: 1 }}
              />
            )}
            {release.title && (
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  color: isLatest ? '#ffffff' : 'text.primary',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {release.title}
              </Typography>
            )}
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pt: 1, pb: 2.5, borderTop: 1, borderColor: 'divider' }}>
          {release.body ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {release.body}
            </ReactMarkdown>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No release notes for this version.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

const ChangelogPage: React.FC = () => {
  const theme = useTheme();
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  useEffect(() => {
    let cancelled = false;
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const url = `${base}/CHANGELOG.md`;
    fetch(url, { cache: 'no-cache' })
      .then((r) => {
        if (!r.ok) throw new Error(`Could not load changelog (HTTP ${r.status})`);
        return r.text();
      })
      .then((t) => {
        if (!cancelled) setText(t);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load changelog');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const releases = useMemo(() => (text ? parseChangelog(text) : []), [text]);
  const visibleReleases = releases.slice(0, visibleCount);
  const hasMore = visibleCount < releases.length;

  return (
    <Box sx={{ width: '100%', p: { xs: 0, sm: 1 } }}>
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 0, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: '#1e3a5c', px: 3, py: 1.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
            Release notes
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              Deployment history for SonOps. Sourced from repository{' '}
              <Box
                component="code"
                sx={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '0.8rem',
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  px: 0.5,
                  borderRadius: 0.5,
                }}
              >
                CHANGELOG.md
              </Box>{' '}
              at build time.
            </Typography>
            {!loading && releases.length > 0 && (
              <Chip
                label={`${releases.length} releases`}
                size="small"
                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main', fontWeight: 600 }}
              />
            )}
          </Stack>
        </Box>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={36} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && releases.length === 0 && text && (
        <Alert severity="info">No structured release entries were found in the changelog file.</Alert>
      )}

      {!loading && !error && visibleReleases.length > 0 && (
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              display: { xs: 'none', sm: 'block' },
              position: 'absolute',
              left: 15,
              top: 8,
              bottom: 8,
              width: 2,
              bgcolor: alpha(theme.palette.secondary.main, 0.25),
            }}
          />

          {visibleReleases.map((release, index) => (
            <ReleaseTimelineItem
              key={`${release.version}-${release.date}-${index}`}
              release={release}
              isLatest={index === 0}
              defaultExpanded={index === 0}
            />
          ))}

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pl: { xs: 0, sm: 4 } }}>
              <Button
                variant="outlined"
                onClick={() => setVisibleCount((n) => Math.min(n + INITIAL_VISIBLE, releases.length))}
              >
                Show older releases ({releases.length - visibleCount} remaining)
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ChangelogPage;
